/*
  FINJAN RUSH — game controller + Phaser scene.
  Loaded ONLY on the dedicated Finjan Rush page (see sections/finjan-rush.liquid).
  Everything here is scoped to <finjan-rush-app>; nothing touches global
  theme JS. This file defines no globals except the custom element tag and
  (reference tests, see bottom of file) window.FinjanRushTestInternals, a
  pure-function surface for the local test harness. Nothing in it is a
  secret -- it exposes the same logic already fully visible in this source
  file, guarded against double-registration.

  The platform issues the challenge and manifest, verifies OTP and results,
  and owns lifetime eligibility and points. This client submits an untrusted
  proof conforming to docs/integrations/finjan-rush-proof-v1.md.
*/
(function () {
  'use strict';

  if (window.customElements.get('finjan-rush-app')) return;

  // Scoring-contract version, independent of theme/asset versioning. Bump
  // this whenever a change affects what "plausible" means to the
  // server validator: CONFIG.scoring, CONFIG.combo, CONFIG.difficultyStages,
  // CONFIG.spawnWeights, CONFIG.gameDurationMs,
  // catch-zone geometry (cupWidthRatio/catchToleranceRatio/the Y-window
  // constants in FinjanRushScene.update), or the result-proof format
  // itself. Do NOT bump for asset/copy/CSS/dev-panel-only changes.
  var GAME_VERSION = 'finjan-rush-scoring-v1';

  /* ------------------------------------------------------------------ */
  /* CONFIG — every tunable number lives here, nowhere else.             */
  /* ------------------------------------------------------------------ */

  var CONFIG = {
    gameDurationMs: 20000,

    scoring: {
      normal: 1,
      golden: 3,
      bag: 6,
      burnt: -5,
      missGolden: -1,
    },

    // Item type weights (must sum to 1). Golden + bag intentionally rare.
    spawnWeights: {
      normal: 0.62,
      burnt: 0.22,
      golden: 0.11,
      bag: 0.05,
    },

    // Staged difficulty curve for the 20-second round — NOT a straight
    // stretch of the old 12-second linear ramp. Four stages (intro / normal
    // / pressure / final rush), each ramping its own start->end values;
    // difficultyAt() interpolates within whichever stage `t` (progress,
    // 0-1) currently falls in. `t` boundaries: 0s, 5s, 10s, 15s, 20s.
    difficultyStages: [
      { t: 0, spawnIntervalMs: 900, fallSpeed: 200 },
      { t: 0.25, spawnIntervalMs: 750, fallSpeed: 260 },
      { t: 0.5, spawnIntervalMs: 550, fallSpeed: 320 },
      { t: 0.75, spawnIntervalMs: 400, fallSpeed: 400 },
      { t: 1, spawnIntervalMs: 260, fallSpeed: 520 },
    ],

    // Target on-screen sprite height (px) per item type — real PNGs are
    // scaled uniformly (aspect-ratio preserved) to these heights, never
    // stretched. Bag reads as the biggest/strongest bonus item; golden is
    // slightly larger than normal/burnt so it stays readable at speed while
    // normal vs. burnt stay the same size so shape/color (not size) is the
    // distinguishing signal.
    itemDisplayHeights: {
      normal: 30,
      burnt: 30,
      golden: 34,
      bag: 54,
    },

    combo: {
      tier1Streak: 5,
      tier1Multiplier: 1.25,
      tier2Streak: 10,
      tier2Multiplier: 1.5,
      perExtraStreak: 0.05,
      max: 2,
    },

    cupWidthRatio: 0.34, // fraction of field width
    catchToleranceRatio: 0.62, // fraction of half cup width, generous but not trivial

    storageKey: 'finjanRushAttempt.v2',
  };

  function difficultyAt(elapsedMs, durationMs) {
    var progress = Phaser.Math.Clamp(elapsedMs / durationMs, 0, 1);
    var stages = CONFIG.difficultyStages;
    for (var i = 0; i < stages.length - 1; i++) {
      var a = stages[i];
      var b = stages[i + 1];
      if (progress >= a.t && progress <= b.t) {
        var segProgress = (progress - a.t) / (b.t - a.t);
        return {
          spawnIntervalMs: Phaser.Math.Linear(a.spawnIntervalMs, b.spawnIntervalMs, segProgress),
          fallSpeed: Phaser.Math.Linear(a.fallSpeed, b.fallSpeed, segProgress),
        };
      }
    }
    var last = stages[stages.length - 1];
    return { spawnIntervalMs: last.spawnIntervalMs, fallSpeed: last.fallSpeed };
  }

  function comboMultiplier(streak) {
    if (streak >= CONFIG.combo.tier2Streak) {
      var extra = (streak - CONFIG.combo.tier2Streak) * CONFIG.combo.perExtraStreak;
      return Math.min(CONFIG.combo.max, CONFIG.combo.tier2Multiplier + extra);
    }
    if (streak >= CONFIG.combo.tier1Streak) return CONFIG.combo.tier1Multiplier;
    return 1;
  }

  // weightedPick now REQUIRES an explicit `rand` source (0..1 generator) --
  // there is no implicit Math.random() fallback. Every official spawn
  // decision must draw from the one seeded PRNG below (see
  // generateSpawnManifest), so a server holding the same seed + game
  // version can regenerate an identical sequence.
  function weightedPick(weights, rand) {
    var r = rand();
    var acc = 0;
    var keys = Object.keys(weights);
    for (var i = 0; i < keys.length; i++) {
      acc += weights[keys[i]];
      if (r <= acc) return keys[i];
    }
    return keys[keys.length - 1];
  }

  /* ------------------------------------------------------------------ */
  /* Reference PRNG retained only for offline parity tests. Official play
     uses the manifest issued by the platform and never creates a seed. */

  function hashSeedToUint32(str) {
    // FNV-1a — deterministically turns an arbitrary seed string into the
    // 32-bit integer state mulberry32 needs.
    var h = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  }

  function createSeededRandom(seedString) {
    var state = hashSeedToUint32(String(seedString));
    return function () {
      state |= 0;
      state = (state + 0x6d2b79f5) | 0;
      var t = Math.imul(state ^ (state >>> 15), 1 | state);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ------------------------------------------------------------------ */
  /* RoundClock — the single elapsed-time source for an official round.  */
  /* Replaces the previous two independent trackers (FinjanRushApp's     */
  /* performance.now()-based HUD timer and FinjanRushScene's own         */
  /* delta-accumulated elapsedMs) with one wall-clock-anchored object     */
  /* that both now read. Wall-clock (performance.now()) is used rather    */
  /* than frame-delta accumulation specifically because it cannot drift   */
  /* under frame drops -- see onVisibilityChange/tickGame's frame-gap     */
  /* check below for how drops/backgrounding are detected against it.     */
  /* ------------------------------------------------------------------ */

  // A gap between two consecutive requestAnimationFrame callbacks larger
  // than this (ms) is treated as an interruption (e.g. tab backgrounded,
  // rAF throttled/paused) rather than ordinary frame jank -- generous
  // enough to not false-positive on a normal ~16.7ms cadence with the
  // occasional dropped frame.
  var FRAME_GAP_INTERRUPT_MS = 250;
  var BOOT_GRACE_MS = 1000;
  function shouldFlagFrameGap(gap, elapsed) { return elapsed > BOOT_GRACE_MS && gap > FRAME_GAP_INTERRUPT_MS; }

  function RoundClock() {
    this.startedAtPerf = null;
    this.state = 'READY'; // completion and interruptions are separate axes
    this.interruptions = [];
    this.invalidReason = null;
  }

  RoundClock.prototype.start = function () {
    this.startedAtPerf = performance.now();
    this.state = 'STARTED';
  };

  RoundClock.prototype.elapsedMs = function () {
    if (this.startedAtPerf === null) return 0;
    return performance.now() - this.startedAtPerf;
  };

  RoundClock.prototype.markInterrupted = function (reason) {
    if (this.interruptions.length < 32) this.interruptions.push({ reason: reason, elapsedMs: Math.round(this.elapsedMs()) });
  };

  RoundClock.prototype.complete = function () { this.state = 'COMPLETED'; };

  RoundClock.prototype.invalidate = function (reason) {
    this.state = 'INVALID';
    this.invalidReason = reason;
  };

  // Deterministically regenerates the full official spawn sequence for a
  // given seed + game version, independent of any live device's real frame
  // timing -- this is what makes the manifest reproducible by the
  // server. Uses a fixed simulated step (nominal 60fps) to walk the SAME
  // deterministic difficulty curve (difficultyAt) the live scene uses for
  // its own real-time spawn cadence check; the two are expected to be very
  // close but are not required to match to the millisecond (see the
  // module doc comment on Option B / plausibility validation, not exact
  // timing replay). Item type and X position are drawn from ONE seeded
  // PRNG in a fixed order, so the manifest is 100% reproducible.
  var MANIFEST_SIMULATION_STEP_MS = 1000 / 60;

  function generateSpawnManifest(seedReference, gameVersion) {
    var rand = createSeededRandom(seedReference + '|' + gameVersion);
    var manifest = [];
    var elapsedMs = 0;
    var spawnAccumulatorMs = 0;
    var spawnId = 0;

    while (elapsedMs <= CONFIG.gameDurationMs) {
      var diff = difficultyAt(elapsedMs, CONFIG.gameDurationMs);
      spawnAccumulatorMs += MANIFEST_SIMULATION_STEP_MS;
      if (spawnAccumulatorMs >= diff.spawnIntervalMs) {
        spawnAccumulatorMs = 0;
        manifest.push({
          spawnId: spawnId,
          scheduledElapsedMs: Math.round(elapsedMs),
          itemType: weightedPick(CONFIG.spawnWeights, rand),
          // Stored as a 0..1 fraction of the playable field width, not an
          // absolute pixel X, so the manifest stays valid regardless of the
          // player's actual viewport (mobile vs. desktop preview, etc.).
          // Live spawnItem() maps this back to a pixel X against the real
          // field width at spawn time.
          xFraction: rand(),
          gameVersion: gameVersion,
        });
        spawnId += 1;
      }
      elapsedMs += MANIFEST_SIMULATION_STEP_MS;
    }
    return manifest;
  }

  /* ------------------------------------------------------------------ */
  /* SoundManager — procedural WebAudio tones as placeholders.          */
  /* Structured so real audio files can be dropped in later by changing */
  /* only playTone() call sites below, never the calling code elsewhere.*/
  /* ------------------------------------------------------------------ */

  function SoundManager() {
    this.ctx = null;
    this.unlocked = false;
  }

  SoundManager.prototype.unlock = function () {
    if (this.unlocked) return;
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.unlocked = true;
  };

  SoundManager.prototype.tone = function (freq, durationMs, type, gainValue) {
    if (!this.ctx) return;
    var ctx = this.ctx;
    var osc = ctx.createOscillator();
    var gain = ctx.createGain();
    osc.type = type || 'sine';
    osc.frequency.value = freq;
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);
    var now = ctx.currentTime;
    var peak = gainValue || 0.06;
    gain.gain.linearRampToValueAtTime(peak, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.02);
  };

  SoundManager.prototype.play = function (name) {
    if (!this.unlocked) return;
    switch (name) {
      case 'catch-normal':
        this.tone(720, 90, 'sine', 0.05);
        break;
      case 'catch-golden':
        this.tone(920, 70, 'triangle', 0.07);
        this.tone(1180, 120, 'triangle', 0.05);
        break;
      case 'catch-bonus':
        this.tone(600, 80, 'square', 0.05);
        this.tone(900, 80, 'square', 0.05);
        this.tone(1200, 160, 'square', 0.05);
        break;
      case 'catch-bad':
        this.tone(180, 220, 'sawtooth', 0.06);
        break;
      case 'countdown-tick':
        this.tone(500, 90, 'sine', 0.04);
        break;
      case 'countdown-go':
        this.tone(880, 200, 'triangle', 0.07);
        break;
      case 'timer-warning':
        this.tone(760, 60, 'square', 0.04);
        break;
      case 'game-over':
        this.tone(440, 120, 'sine', 0.05);
        this.tone(330, 220, 'sine', 0.05);
        break;
      case 'reward-reveal':
        this.tone(660, 90, 'triangle', 0.05);
        this.tone(880, 90, 'triangle', 0.05);
        this.tone(1100, 220, 'triangle', 0.05);
        break;
    }
  };

  function vibrate(pattern) {
    if (navigator.vibrate) {
      try {
        navigator.vibrate(pattern);
      } catch (e) {
        /* no-op — haptics are a nice-to-have, never block gameplay */
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Phaser scene                                                        */
  /* ------------------------------------------------------------------ */

  function createPlaceholderTextures(scene) {
    // All game art comes from the approved Figma assets; only particles are procedural.
    var g = scene.add.graphics();

    // Particle dot
    g.clear();
    g.fillStyle(0xffffff, 1);
    g.fillCircle(4, 4, 4);
    g.generateTexture('fr-particle', 8, 8);

    g.destroy();
  }

  function FinjanRushScene(app) {
    Phaser.Scene.call(this, { key: 'FinjanRush' });
    this.app = app;
  }
  FinjanRushScene.prototype = Object.create(Phaser.Scene.prototype);
  FinjanRushScene.prototype.constructor = FinjanRushScene;

  FinjanRushScene.prototype.preload = function () {
    var urls = this.app.assetUrls || {};
    if (urls.cup) this.load.svg('fr-cup', urls.cup);
    if (urls.normal) this.load.svg('fr-bean-normal', urls.normal);
    if (urls.golden) this.load.svg('fr-bean-golden', urls.golden);
    if (urls.burnt) this.load.svg('fr-bean-burnt', urls.burnt);
    if (urls.bag) this.load.svg('fr-bag', urls.bag);
  };

  FinjanRushScene.prototype.create = function () {
    var scene = this;
    var app = this.app;
    var w = this.scale.width;
    var h = this.scale.height;

    createPlaceholderTextures(this);

    this.reduceMotion = prefersReducedMotion();
    this.running = false;
    // Elapsed time for spawn scheduling/difficulty now comes from the
    // single shared app.roundClock (see RoundClock) instead of a
    // scene-local accumulator, so there's one authoritative clock instead
    // of two that could silently drift apart.
    this.spawnAccumulatorMs = 0;
    this.items = this.add.group();

    var cupWidth = w * CONFIG.cupWidthRatio;
    this.cupHalfWidth = cupWidth / 2;
    this.cupY = h - 46;

    this.cup = this.add.image(w / 2, this.cupY, 'fr-cup');
    this.cup.setDisplaySize(cupWidth, cupWidth * 0.75);
    this.cupTargetX = w / 2;

    this.emitter = this.add.particles(0, 0, 'fr-particle', {
      emitting: false,
      lifespan: 380,
      speed: { min: 60, max: 160 },
      scale: { start: 1, end: 0 },
      quantity: 1,
    });

    // --- Drag input -----------------------------------------------------
    this.input.on('pointerdown', function (pointer) {
      scene.dragging = true;
      scene.cupTargetX = Phaser.Math.Clamp(pointer.x, scene.cupHalfWidth, w - scene.cupHalfWidth);
    });
    this.input.on('pointermove', function (pointer) {
      if (!scene.dragging) return;
      scene.cupTargetX = Phaser.Math.Clamp(pointer.x, scene.cupHalfWidth, w - scene.cupHalfWidth);
    });
    this.input.on('pointerup', function () {
      scene.dragging = false;
    });
    this.input.on('pointerupoutside', function () {
      scene.dragging = false;
    });

    var slider = app.querySelector('[data-role="cup-control"]');
    slider.addEventListener('input', function () {
      scene.cupTargetX = scene.cupHalfWidth + (w - scene.cupHalfWidth * 2) * Number(slider.value) / 100;
    });
    app.onSceneReady(this);
  };

  FinjanRushScene.prototype.startRun = function () {
    this.running = true;
    this.spawnAccumulatorMs = 0;
    this.items.clear(true, true);
  };

  FinjanRushScene.prototype.stopRun = function () {
    this.running = false;
    this.items.getChildren().slice().forEach(function (item) {
      item.destroy();
    });
  };

  FinjanRushScene.prototype.spawnItem = function (entry) {
    var w = this.scale.width;
    // Item type, horizontal position and timing come from the server manifest.
    if (!entry) return; // manifest exhausted -- generated with headroom, should not normally happen
    var type = entry.itemType;
    var textureKey =
      type === 'golden' ? 'fr-bean-golden' : type === 'burnt' ? 'fr-bean-burnt' : type === 'bag' ? 'fr-bag' : 'fr-bean-normal';

    var margin = 26;
    var x = Math.round(margin + entry.xFraction * (w - margin * 2));
    var item = this.add.image(x, -30, textureKey);
    // Uniform scale (never setDisplaySize with mismatched w/h) preserves the
    // source PNG's real aspect ratio — no stretching/distortion.
    var targetHeight = CONFIG.itemDisplayHeights[type];
    var scale = targetHeight / item.height;
    item.setScale(scale);
    item.itemType = type; // kept on the sprite for burst()/visuals only -- not scoring-authoritative
    item.spawnId = entry.spawnId;
    item.caught = false;
    item.missed = false;
    this.items.add(item);
    this.app.onSpawn(type, entry.spawnId);
  };

  FinjanRushScene.prototype.update = function (time, delta) {
    if (!this.running) return;

    // Smooth cup follow (tactile, not instant snap).
    var followSpeed = 0.28;
    this.cup.x += (this.cupTargetX - this.cup.x) * followSpeed;

    // Elapsed time for difficulty/spawn-manifest indexing comes from the
    // one shared RoundClock (wall-clock, immune to frame-delta drift).
    var elapsedMs = this.app.roundClock ? this.app.roundClock.elapsedMs() : 0;
    var diff = difficultyAt(elapsedMs, CONFIG.gameDurationMs);
    var spawnInterval = diff.spawnIntervalMs;
    var fallSpeed = diff.fallSpeed;

    // Follow the server's frozen manifest schedule, including low-FPS catch-up.
    var due = this.app.takeDueManifestEntries(Math.min(elapsedMs, CONFIG.gameDurationMs));
    if (due.length > 1) this.app.roundClock.markInterrupted('manifest_catch_up');
    for (var d = 0; d < due.length; d++) this.spawnItem(due[d]);

    var h = this.scale.height;
    var scene = this;
    this.items.getChildren().slice().forEach(function (item) {
      item.y += (fallSpeed * delta) / 1000;

      var withinCatchY = item.y >= scene.cupY - 34 && item.y <= scene.cupY + 8;
      var withinCatchX = Math.abs(item.x - scene.cup.x) <= scene.cupHalfWidth * CONFIG.catchToleranceRatio;

      if (!item.caught && withinCatchY && withinCatchX) {
        item.caught = true;
        scene.app.onCatch(item.spawnId, item.x, item.y);
        scene.burst(item.x, item.y, item.itemType);
        item.destroy();
        return;
      }

      if (item.y > h + 30) {
        if (!item.caught && !item.missed) {
          item.missed = true;
          scene.app.onMiss(item.spawnId);
        }
        item.destroy();
      }
    });
  };

  FinjanRushScene.prototype.burst = function (x, y, type) {
    if (this.reduceMotion) return;
    var tint = type === 'golden' ? 0xc2984a : type === 'bag' ? 0x6b432c : type === 'burnt' ? 0x8a3324 : 0x2d1f18;
    this.emitter.setParticleTint(tint);
    this.emitter.explode(type === 'bag' ? 14 : type === 'golden' ? 10 : 6, x, y);

    var flashScale = type === 'bag' ? 1.6 : type === 'golden' ? 1.35 : 1.12;
    this.tweens.add({
      targets: this.cup,
      scaleX: this.cup.scaleX * flashScale,
      scaleY: this.cup.scaleY * (flashScale - (flashScale - 1) * 0.4),
      duration: 90,
      yoyo: true,
      ease: 'Sine.easeOut',
    });
  };

  /* ------------------------------------------------------------------ */
  /* Custom element — screen state machine + platform API                */
  /* ------------------------------------------------------------------ */

  function FinjanRushApp() {
    return Reflect.construct(HTMLElement, [], FinjanRushApp);
  }
  FinjanRushApp.prototype = Object.create(HTMLElement.prototype);
  FinjanRushApp.prototype.constructor = FinjanRushApp;

  FinjanRushApp.prototype.connectedCallback = function () {
    if (this._initialized) return;
    this._initialized = true;
    this.api = new window.FinjanRushApi.Client(this.dataset.apiOrigin, this.dataset.platformEnabled === 'true');
    this.sound = new SoundManager();
    this.devMode = this.dataset.devMode === 'true' && ['localhost', '127.0.0.1'].indexOf(location.hostname) !== -1;
    this.assetUrls = {
      cup: this.dataset.assetCup || '',
      bag: this.dataset.assetBag || '',
      normal: this.dataset.assetNormal || '',
      golden: this.dataset.assetGolden || '',
      burnt: this.dataset.assetBurnt || '',
    };
    this.phoneNumber = '';
    this.stats = this.freshStats();
    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;

    // Result-proof scaffold state (see module doc comment). Reset per
    // round in startGame(); kept here only as the pre-first-round default.
    this.roundClock = null;
    this.spawnManifest = [];
    this._manifestCursor = 0;
    this._pendingSpawns = {};
    this.eventLog = [];
    this.currentChallengeRef = null;
    this.lastResultProof = null;
    this._lastTickNow = null;

    this.screens = {};
    this.querySelectorAll('.finjan-rush__screen').forEach(
      function (el) {
        this.screens[el.dataset.screen] = el;
      }.bind(this)
    );

    this.bindStart();
    this.bindPhone();
    this.bindOtp();
    this.bindReward();
    this.bindPlayed();

    this.hydrateHighScore();

    if (this.devMode) this.buildDevPanel();

    // Registered once for the element's lifetime (not per-round) --
    // onVisibilityChange itself only acts while a round is actually
    // STARTED, so this is a harmless no-op the rest of the time.
    this._boundOnVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this._boundOnVisibilityChange);
  };

  FinjanRushApp.prototype.disconnectedCallback = function () {
    window.clearInterval(this._resendInterval);
    window.clearTimeout(this._countdownTimer);
    window.cancelAnimationFrame(this._rafId);
    if (this._openInitialInstructions) document.removeEventListener('DOMContentLoaded', this._openInitialInstructions);
    if (this.instructions && this.instructions.open) this.instructions.close();
    if (this.game) this.game.destroy(true);
    if (this.sound.ctx) this.sound.ctx.close();
    this.api.gameToken = null;
    this.api.csrf = null;
    this.lastResultProof = null;
    if (this._boundOnVisibilityChange) {
      document.removeEventListener('visibilitychange', this._boundOnVisibilityChange);
    }
  };

  // Local-only interruption detection (section 7 of the scaffold spec).
  // This never pauses gameplay, never consumes a local "attempt", and
  // never blocks a round from finishing -- it only records that something
  // anomalous happened, for the platform to weigh when deciding whether
  // an interrupted official session may resume or must be replayed.
  FinjanRushApp.prototype.onVisibilityChange = function () {
    if (document.hidden && this.roundClock && this.roundClock.state === 'STARTED') {
      this.roundClock.markInterrupted('tab_hidden');
    }
  };

  FinjanRushApp.prototype.freshStats = function () {
    return {
      normalSpawned: 0,
      normalCaught: 0,
      goldenSpawned: 0,
      goldenCaught: 0,
      bagSpawned: 0,
      bagCaught: 0,
      burntSpawned: 0,
      burntCaught: 0,
      frameSamples: [],
      lastFrameTs: 0,
    };
  };

  FinjanRushApp.prototype.showScreen = function (name) {
    if (name !== 'start' && this.instructions && this.instructions.open) this.instructions.close();
    Object.keys(this.screens).forEach(
      function (key) {
        this.screens[key].hidden = key !== name;
      }.bind(this)
    );
    this.dataset.activeScreen = name;
    var focusTarget = this.screens[name].querySelector('h1, h2, [tabindex]') || this.screens[name];
    if (focusTarget) {
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    }
  };

  FinjanRushApp.prototype.hydrateHighScore = function () {
    var pillEl = this.querySelector('[data-role="high-score-pill"]');
    if (!pillEl) return;
    var high = 0;
    try {
      var all = JSON.parse(localStorage.getItem(CONFIG.storageKey + '.allTime') || '0');
      high = Number.isInteger(all) && all >= 0 ? all : 0;
    } catch (e) {
      high = 0;
    }
    pillEl.textContent = 'Best on this device ' + high;
  };

  FinjanRushApp.prototype.setNotice = function (message) {
    var notice = this.querySelector('[data-role="notice"]');
    notice.textContent = message || '';
    notice.hidden = !message;
  };

  FinjanRushApp.prototype.errorMessage = function (error) {
    if (error.code === 'NOT_ENABLED' || error.code === 'NOT_FOUND') return 'Official play is not available yet. Please check back soon.';
    if (error.code === 'VERIFICATION_FAILED') return 'That code could not be verified. Check it or request a new code.';
    if (error.code === 'RATE_LIMITED') return 'Please wait before trying again.';
    if (error.code === 'OTP_EXPIRED') return 'Your code has expired. Request a new code.';
    if (error.code === 'OTP_UNAVAILABLE' || error.code === 'OTP_DELIVERY_UNAVAILABLE') return 'We could not send your code. Please try again shortly.';
    if (error.code === 'AUTHENTICATION_REQUIRED' || error.code === 'CSRF_FAILED') return 'Your session has expired. Verify your number again.';
    if (error.code === 'ACTIVE_SESSION') return 'An official run is already in progress. Keep playing in its original tab. A new run cannot be started here.';
    if (error.code === 'GAME_SESSION_EXPIRED') return 'This game session has expired. Your points account will show any confirmed result.';
    if (error.code === 'PLAY_NOT_AVAILABLE') return 'Official play is not available for this account. You can still view your points.';
    return 'We could not connect. Please try again.';
  };

  FinjanRushApp.prototype.checkEligibility = async function () {
    var result = await this.api.status();
    if (result.status === 'ALREADY_PLAYED' && result.hasPlayed) {
      this.renderPlayed(result);
      this.showScreen('played');
      return;
    }
    if (result.status === 'ACTIVE_SESSION' && !this.officialSession) throw {code: 'ACTIVE_SESSION'};
    if (!result.eligible || ['ELIGIBLE', 'ACTIVE_SESSION'].indexOf(result.status) === -1) throw {code: 'PLAY_NOT_AVAILABLE'};
    this.officialSession = await this.api.issue(GAME_VERSION, CONFIG.gameDurationMs);
    this.beginCountdown();
  };

  FinjanRushApp.prototype.bindStart = function () {
    var self = this;
    var btn = this.querySelector('[data-action="go-phone"]');
    this.instructions = this.querySelector('[data-role="instructions"]');
    function openInstructions() {
      if (!self.isConnected || self._busy || self.screens.start.hidden || self.instructions.open) return;
      self.instructions.showModal();
      self.querySelector('.finjan-rush__instructions-body').scrollTop = 0;
    }
    btn.addEventListener('click', openInstructions);
    this.querySelector('[data-action="close-instructions"]').addEventListener('click', function () {
      self.instructions.close();
    });
    this.instructions.addEventListener('close', function () {
      if (!self.screens.start.hidden) btn.focus({ preventScroll: true });
    });
    this.querySelector('[data-action="confirm-instructions"]').addEventListener('click', function () {
      if (self._busy || self.screens.start.hidden || !self.instructions.open) return;
      self.instructions.close();
      self.startAfterInstructions();
    });

    // Check a returning member without issuing a session, requesting OTP, or
    // starting a round. Completed players go straight to their saved result.
    async function restoreInitialView() {
      if (!self.isConnected || self.screens.start.hidden) return;
      if (self.api.enabled) {
        self._busy = true; btn.disabled = true;
        try {
          await self.api.restore();
          var result = await self.api.status();
          if (!self.isConnected || self.screens.start.hidden) return;
          if (result.status === 'ALREADY_PLAYED' && result.hasPlayed) {
            self.renderPlayed(result); self.showScreen('played'); return;
          }
          if (result.status === 'ACTIVE_SESSION') {
            self.setNotice(self.errorMessage({code:'ACTIVE_SESSION'})); return;
          }
        } catch (error) {
          if (self.isConnected && error.code !== 'AUTHENTICATION_REQUIRED') self.setNotice(self.errorMessage(error));
        } finally { self._busy = false; btn.disabled = false; }
      }
      openInstructions();
    }
    this._openInitialInstructions = restoreInitialView;
    if (document.readyState === 'complete') restoreInitialView();
    else document.addEventListener('DOMContentLoaded', restoreInitialView, { once: true });
  };

  FinjanRushApp.prototype.startAfterInstructions = async function () {
    if (this._busy) return;
    var btn = this.querySelector('[data-action="go-phone"]');
    this._busy = true; btn.disabled = true; this.setNotice('');
    this.sound.unlock();
    try {
      try { await this.api.restore(); }
      catch (error) {
        if (error.code !== 'AUTHENTICATION_REQUIRED') throw error;
        this.showScreen('phone');
        return;
      }
      await this.checkEligibility();
    } catch (error) { this.setNotice(this.errorMessage(error)); }
    finally { this._busy = false; btn.disabled = false; }
  };

  FinjanRushApp.prototype.requestCode = async function () {
    var challenge = await this.api.challenge(this.phoneNumber);
    this.verificationToken = challenge.verificationToken;
    this.codeExpiresAt = Date.now() + challenge.expiresInSeconds * 1000;
    this.querySelectorAll('[data-otp-digit]').forEach(function (box) { box.value = ''; box.classList.remove('is-filled'); });
    this.querySelector('[data-action="verify-otp"]').disabled = true;
    this.querySelector('[data-role="otp-subtitle"]').textContent = 'Enter the 6-digit verification code sent to ' + this.maskedPhone() + '.';
    this.showScreen('otp');
    this.startResendTimer(challenge.resendAfterSeconds);
  };

  FinjanRushApp.prototype.bindPhone = function () {
    var self = this;
    var form = this.querySelector('[data-form="phone"]');
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (self._busy) return;
      var input = self.querySelector('[name="phone"]');
      var digits = window.FinjanRushApi.normalizeDigits(input.value).replace(/[\s()-]/g, '');
      if (!/^[569]\d{7}$/.test(digits)) {
        input.setAttribute('aria-invalid', 'true'); input.focus();
        self.setNotice('Enter a valid 8-digit Kuwait mobile number.'); return;
      }
      input.removeAttribute('aria-invalid');
      self.phoneNumber = '+965' + digits;
      var btn = form.querySelector('[data-action="send-code"]');
      self._busy = true; btn.disabled = true; btn.textContent = 'Sending…'; self.setNotice('');
      try { await self.requestCode(); }
      catch (error) { self.setNotice(self.errorMessage(error)); }
      finally { self._busy = false; btn.disabled = false; btn.textContent = 'Send Code'; }
    });
  };

  FinjanRushApp.prototype.maskedPhone = function () {
    return '+965 ' + this.phoneNumber.slice(4, 5) + 'XXX XX' + this.phoneNumber.slice(-2);
  };

  FinjanRushApp.prototype.bindOtp = function () {
    var self = this;
    var boxes = Array.prototype.slice.call(this.querySelectorAll('[data-otp-digit]'));
    var verifyBtn = this.querySelector('[data-action="verify-otp"]');
    var form = this.querySelector('[data-form="otp"]');
    function currentCode() { return boxes.map(function (b) { return b.value; }).join(''); }
    function refresh() { verifyBtn.disabled = self._busy || currentCode().length !== 6; }
    function fill(text, offset) {
      text = window.FinjanRushApi.normalizeDigits(text).replace(/\D/g, '');
      for (var j = offset; j < boxes.length; j++) {
        boxes[j].value = text[j - offset] || ''; boxes[j].classList.toggle('is-filled', !!boxes[j].value);
      }
      (boxes[Math.min(offset + text.length, 5)]).focus(); refresh();
    }
    boxes.forEach(function (box, i) {
      box.addEventListener('input', function () {
        var digits = window.FinjanRushApi.normalizeDigits(box.value).replace(/\D/g, '');
        if (digits.length > 1) { fill(digits, i); return; }
        box.value = digits; box.classList.toggle('is-filled', !!digits);
        if (digits && boxes[i + 1]) boxes[i + 1].focus(); refresh();
      });
      box.addEventListener('keydown', function (e) {
        if (e.key === 'Backspace' && !box.value && boxes[i - 1]) boxes[i - 1].focus();
      });
      box.addEventListener('paste', function (e) { e.preventDefault(); fill(e.clipboardData.getData('text'), i); });
    });
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (self._busy || currentCode().length !== 6) return;
      self._busy = true; verifyBtn.disabled = true; verifyBtn.textContent = 'Verifying…'; self.setNotice('');
      try {
        if (Date.now() >= self.codeExpiresAt) throw {code: 'OTP_EXPIRED'};
        await self.api.verify(self.verificationToken, currentCode());
        self.verificationToken = null; self.phoneNumber = '';
        boxes.forEach(function (box) { box.value = ''; });
        window.clearInterval(self._resendInterval);
        // If eligibility fails, Continue retries with the verified session;
        // it never attempts to verify the already-consumed OTP again.
        self.showScreen('start');
        await self.checkEligibility();
      } catch (error) { self.setNotice(self.errorMessage(error)); }
      finally { self._busy = false; verifyBtn.textContent = 'Verify Number'; refresh(); }
    });
    var resend = this.querySelector('[data-role="resend-timer"]');
    resend.addEventListener('click', async function () {
      if (self._busy || Date.now() < self.resendAt) return;
      self._busy = true; resend.disabled = true; self.setNotice(''); refresh();
      try { await self.requestCode(); }
      catch (error) { self.setNotice(self.errorMessage(error)); self.startResendTimer(error.retryAfter || 1); }
      finally { self._busy = false; refresh(); }
    });
    this.querySelector('[data-action="change-phone"]').addEventListener('click', function () {
      if (self._busy) return;
      window.clearInterval(self._resendInterval); self.verificationToken = null;
      self.showScreen('phone'); self.setNotice('');
    });
  };

  FinjanRushApp.prototype.startResendTimer = function (seconds) {
    var self = this;
    var el = this.querySelector('[data-role="resend-timer"]');
    this.resendAt = Date.now() + Math.max(0, seconds) * 1000;
    window.clearInterval(this._resendInterval);
    function update() {
      var remaining = Math.max(0, Math.ceil((self.resendAt - Date.now()) / 1000));
      el.disabled = remaining > 0 || self._busy;
      el.textContent = remaining ? 'Resend code in ' + String(Math.floor(remaining / 60)).padStart(2, '0') + ':' + String(remaining % 60).padStart(2, '0') : 'Resend code';
      if (!remaining && !self._busy) window.clearInterval(self._resendInterval);
    }
    update(); this._resendInterval = window.setInterval(update, 1000);
  };

  /* ---- Screen 4: Countdown ---- */

  FinjanRushApp.prototype.beginCountdown = function () {
    var self = this;
    this.showScreen('countdown');
    var numberEl = this.querySelector('[data-role="countdown-number"]');
    var sequence = ['3', '2', '1', 'Go'];
    var i = 0;

    function step() {
      numberEl.textContent = sequence[i];
      numberEl.classList.remove('is-changing');
      // Force reflow so the animation restarts every tick.
      void numberEl.offsetWidth;
      numberEl.classList.add('is-changing');
      self.sound.play(sequence[i] === 'Go' ? 'countdown-go' : 'countdown-tick');
      vibrate(sequence[i] === 'Go' ? 30 : 12);

      i += 1;
      if (i < sequence.length) {
        self._countdownTimer = window.setTimeout(step, 700);
      } else {
        self._countdownTimer = window.setTimeout(async function () {
          self.showScreen('play');
          try { await self.prepareOfficialGame(); }
          catch (error) { self.showScreen('start'); self.setNotice(self.errorMessage(error)); }
        }, 500);
      }
    }
    step();
  };

  /* ---- Screen 5: Gameplay ---- */

  FinjanRushApp.prototype.onSceneReady = function (scene) {
    this.scene = scene;
    // Phaser.Game boots asynchronously — create() can fire after startGame()
    // has already tried (and silently failed) to call startRun() on a
    // not-yet-existing scene. If a start was requested before the scene was
    // ready, honor it the instant it becomes available.
    if (this._pendingStart) {
      this._pendingStart = false;
      this.scene.startRun();
    }
  };

  FinjanRushApp.prototype.ensurePhaser = function () {
    if (this.game) return;
    var mount = this.querySelector('[data-role="game-canvas-mount"]');
    var fieldEl = this.querySelector('[data-role="game-field"]');
    var rect = fieldEl.getBoundingClientRect();
    var app = this;

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: mount,
      width: Math.max(280, Math.round(rect.width)),
      height: Math.max(360, Math.round(rect.height)),
      transparent: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      scale: { mode: Phaser.Scale.NONE },
      scene: new FinjanRushScene(app),
      render: { antialias: true, transparent: true },
    });

    // Prevent page scroll while a drag gesture is active inside the field,
    // and restore normal scrolling the instant the pointer lifts or leaves.
    var blockScroll = function (e) {
      if (app.dragActive) e.preventDefault();
    };
    fieldEl.addEventListener('touchstart', function () { app.dragActive = true; }, { passive: true });
    fieldEl.addEventListener('touchend', function () { app.dragActive = false; }, { passive: true });
    fieldEl.addEventListener('touchcancel', function () { app.dragActive = false; }, { passive: true });
    fieldEl.addEventListener('touchmove', blockScroll, { passive: false });
  };

  FinjanRushApp.prototype.startGame = function () {
    this.ensurePhaser();
    this.sound.unlock();

    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.stats = this.freshStats();
    this.timeRemainingMs = CONFIG.gameDurationMs;
    this.updateHud();
    this.updateCombo();

    var comboBar = this.querySelector('[data-role="combo-bar"]');
    comboBar.classList.remove('is-hot');

    this.currentChallengeRef = this.officialSession.challengeRef;
    this.spawnManifest = this.officialSession.manifest;
    this._manifestCursor = 0;
    this._pendingSpawns = {};
    this.eventLog = [];
    this.roundClock = new RoundClock();
    this.roundClock.start();
    this._lastTickNow = null;

    if (this.scene) {
      this.scene.startRun();
    } else {
      this._pendingStart = true;
    }

    this._rafId = window.requestAnimationFrame(this.tickGame.bind(this));

    this._warned = false;
  };

  FinjanRushApp.prototype.tickGame = function (now) {
    // Large gap between consecutive rAF callbacks (tab backgrounded, rAF
    // throttled/paused, major stall) is recorded as an interruption. The
    // round-clock's wall-clock elapsed keeps advancing regardless -- this
    // detection doesn't pause anything, it only flags that something
    // anomalous happened for the platform to weigh.
    if (this._lastTickNow !== null) {
      var frameGap = now - this._lastTickNow;
      if (shouldFlagFrameGap(frameGap, this.roundClock.elapsedMs()) && this.roundClock.state === 'STARTED') {
        this.roundClock.markInterrupted('frame_gap', Math.round(frameGap));
      }
    }
    this._lastTickNow = now;

    var elapsed = this.roundClock.elapsedMs();
    this.timeRemainingMs = Math.max(0, CONFIG.gameDurationMs - elapsed);

    // Frame-stability sampling for dev balance mode.
    if (this.stats.lastFrameTs) {
      this.stats.frameSamples.push(now - this.stats.lastFrameTs);
      if (this.stats.frameSamples.length > 240) this.stats.frameSamples.shift();
    }
    this.stats.lastFrameTs = now;

    this.updateHud();

    if (!this._warned && this.timeRemainingMs <= 3000) {
      this._warned = true;
      this.sound.play('timer-warning');
    }

    if (this.timeRemainingMs <= 0) {
      this.endGame();
      return;
    }
    this._rafId = window.requestAnimationFrame(this.tickGame.bind(this));
  };

  FinjanRushApp.prototype.updateHud = function () {
    var scoreEl = this.querySelector('[data-role="hud-score"]');
    var timeEl = this.querySelector('[data-role="hud-time"]');
    scoreEl.textContent = Math.round(this.score);
    var seconds = Math.ceil(this.timeRemainingMs / 1000);
    timeEl.textContent = '00:' + String(seconds).padStart(2, '0');
  };

  FinjanRushApp.prototype.updateCombo = function () {
    var streakEl = this.querySelector('[data-role="combo-streak"]');
    var multEl = this.querySelector('[data-role="combo-multiplier"]');
    var bar = this.querySelector('[data-role="combo-bar"]');
    var mult = comboMultiplier(this.combo);
    // IMPORTANT: streak (consecutive catches) and multiplier (actual score
    // multiplier, capped at CONFIG.combo.max) are always rendered as two
    // separate, clearly-labeled values — never collapse them into a single
    // "Combo ×N" where N is really just the catch streak.
    streakEl.textContent = 'Streak ' + this.combo;
    if (mult > 1) {
      multEl.textContent = '×' + mult.toFixed(2).replace(/\.?0+$/, '');
      multEl.hidden = false;
    } else {
      multEl.hidden = true;
    }
    bar.classList.toggle('is-hot', mult > 1);
  };

  // --- Result-proof scaffold helpers ------------------------------------
  // These three methods are the only bridge between live gameplay and the
  // manifest/event-log: spawnItem() pulls the next manifest entry, onSpawn
  // registers it as pending, and onCatch/onMiss resolve it. See the module
  // doc comment.

  FinjanRushApp.prototype.takeDueManifestEntries = function (elapsed) {
    var due = [];
    while (this._manifestCursor < this.spawnManifest.length && this.spawnManifest[this._manifestCursor].scheduledElapsedMs <= elapsed) {
      due.push(this.spawnManifest[this._manifestCursor++]);
    }
    return due;
  };

  FinjanRushApp.prototype.prepareOfficialGame = async function () {
    this.ensurePhaser();
    await new Promise(function (resolve, reject) {
      var until = Date.now() + 15000;
      var check = function () {
        if (!this.isConnected) return reject({code: 'NETWORK_ERROR'});
        if (this.scene) {
          var keys = ['fr-cup', 'fr-bag', 'fr-bean-normal', 'fr-bean-golden', 'fr-bean-burnt'];
          if (!keys.every(function (key) { return this.scene.textures.exists(key); }.bind(this))) return reject({code: 'ASSETS_UNAVAILABLE'});
          return resolve();
        }
        if (Date.now() > until) return reject({code: 'ASSETS_UNAVAILABLE'});
        this._countdownTimer = window.setTimeout(check, 30);
      }.bind(this);
      check();
    }.bind(this));
    await this.api.start();
    if (this.isConnected) this.startGame();
  };

  FinjanRushApp.prototype.registerSpawn = function (spawnId, itemType) {
    this._pendingSpawns[spawnId] = { itemType: itemType, resolved: false };
  };

  // Records at most one outcome per spawnId. Unknown or already-resolved
  // spawnIds are ignored (returns null) -- this reduces accidental/direct
  // method misuse (calling onCatch/onMiss with a made-up or reused spawnId
  // does nothing), but this is NOT a security boundary: nothing running in
  // the same browser as the game it's validating can be one. The real
  // protection is the platform independently regenerating the
  // manifest and replaying this event log itself.
  FinjanRushApp.prototype.recordOutcome = function (spawnId, outcome, elapsedMs) {
    var pending = this._pendingSpawns[spawnId];
    if (!pending || pending.resolved) return null;
    pending.resolved = true;
    // Deliberately NOT storing itemType or comboAtEvent on the event --
    // the event log stays exactly {spawnId, outcome, elapsedMs}. The
    // validator derives item type from the regenerated manifest and combo
    // state by replaying prior events itself; nothing client-computed is
    // treated as authoritative here.
    var event = { spawnId: spawnId, outcome: outcome, elapsedMs: Math.min(CONFIG.gameDurationMs, Math.round(elapsedMs)) };
    this.eventLog.push(event);
    return pending.itemType;
  };

  FinjanRushApp.prototype.onSpawn = function (type, spawnId) {
    this.registerSpawn(spawnId, type);
    var stats = this.stats;
    if (type === 'normal') stats.normalSpawned += 1;
    else if (type === 'golden') stats.goldenSpawned += 1;
    else if (type === 'bag') stats.bagSpawned += 1;
    else if (type === 'burnt') stats.burntSpawned += 1;
    this.refreshDevPanel();
  };

  FinjanRushApp.prototype.onCatch = function (spawnId, x, y) {
    // Item type is derived from the registered spawn record, never taken
    // as a free-form argument -- see recordOutcome's doc comment.
    var type = this.recordOutcome(spawnId, 'catch', this.roundClock ? this.roundClock.elapsedMs() : 0);
    if (!type) return; // unknown/duplicate spawnId -- ignored

    var stats = this.stats;
    var points = 0;
    var soundName = 'catch-normal';
    var popClass = 'finjan-rush__pop--normal';
    var popText = '';
    var breaksCombo = type === 'burnt';

    if (type === 'normal') {
      stats.normalCaught += 1;
      points = CONFIG.scoring.normal;
      popText = '+1';
    } else if (type === 'golden') {
      stats.goldenCaught += 1;
      points = CONFIG.scoring.golden;
      soundName = 'catch-golden';
      popClass = 'finjan-rush__pop--golden';
      popText = '+3';
    } else if (type === 'bag') {
      stats.bagCaught += 1;
      points = CONFIG.scoring.bag;
      soundName = 'catch-bonus';
      popClass = 'finjan-rush__pop--bonus';
      popText = '+6';
    } else if (type === 'burnt') {
      stats.burntCaught += 1;
      points = CONFIG.scoring.burnt;
      soundName = 'catch-bad';
      popClass = 'finjan-rush__pop--bad';
      popText = '-5';
    }

    if (breaksCombo) {
      this.combo = 0;
      this.score += points; // penalty, not multiplied
      vibrate(60);
    } else {
      var mult = comboMultiplier(this.combo);
      var awarded = Math.round(points * mult);
      this.score += awarded;
      popText = (awarded >= 0 ? '+' : '') + awarded;
      this.combo += 1;
      this.maxCombo = Math.max(this.maxCombo, this.combo);
      vibrate(type === 'normal' ? 10 : type === 'golden' ? 18 : 26);
    }

    this.updateCombo();
    this.updateHud();
    this.sound.play(soundName);
    this.spawnPop(popText, popClass, x, y);
    this.refreshDevPanel();
  };

  FinjanRushApp.prototype.onMiss = function (spawnId) {
    // Item type is derived from the registered spawn record, same as
    // onCatch -- see recordOutcome's doc comment.
    var type = this.recordOutcome(spawnId, 'miss', this.roundClock ? this.roundClock.elapsedMs() : 0);
    if (!type) return; // unknown/duplicate spawnId -- ignored

    // Spawn counts are tracked at spawn time (onSpawn) so the dev panel
    // stays accurate mid-round. This handler only applies score effects —
    // missing a normal or burnt bean is neutral (avoiding a burnt bean is
    // correct play), missing a golden bean has a small penalty.
    if (type === 'golden') {
      this.score += CONFIG.scoring.missGolden;
      this.updateHud();
    }
    this.refreshDevPanel();
  };

  FinjanRushApp.prototype.spawnPop = function (text, className, gameX, gameY) {
    if (prefersReducedMotion()) return;
    var field = this.querySelector('[data-role="game-field"]');
    var canvas = field.querySelector('canvas');
    if (!canvas) return;
    var rect = canvas.getBoundingClientRect();
    var fieldRect = field.getBoundingClientRect();
    var scaleX = rect.width / (this.scene ? this.scene.scale.width : rect.width);
    var scaleY = rect.height / (this.scene ? this.scene.scale.height : rect.height);

    var pop = document.createElement('span');
    pop.className = 'finjan-rush__pop ' + className;
    pop.textContent = text;
    pop.style.left = rect.left - fieldRect.left + gameX * scaleX + 'px';
    pop.style.top = rect.top - fieldRect.top + gameY * scaleY + 'px';
    field.appendChild(pop);
    window.setTimeout(function () {
      pop.remove();
    }, 650);
  };

  FinjanRushApp.prototype.endGame = function () {
    if (this.roundClock.state === 'COMPLETED') return;
    window.cancelAnimationFrame(this._rafId);
    if (this.scene) this.scene.stopRun();
    this.sound.play('game-over');
    // Items due at the boundary or still in flight get the contract's zero-score
    // round_end outcome. No omissions, fabricated catches, or extra penalties.
    this.takeDueManifestEntries(CONFIG.gameDurationMs).forEach(function (entry) { this.registerSpawn(entry.spawnId, entry.itemType); }.bind(this));
    Object.keys(this._pendingSpawns).forEach(function (id) { this.recordOutcome(Number(id), 'round_end', CONFIG.gameDurationMs); }.bind(this));
    this.roundClock.complete();
    this.lastResultProof = {
      challengeRef: this.currentChallengeRef, gameVersion: GAME_VERSION,
      clientDurationMs: Math.round(this.roundClock.elapsedMs()),
      finalScoreClaimed: Math.max(0, Math.round(this.score)),
      events: this.eventLog.slice().sort(function (a, b) { return a.elapsedMs - b.elapsedMs || a.spawnId - b.spawnId; }),
      completionState: this.roundClock.state, interruptions: this.roundClock.interruptions.slice(),
    };
    this.showScreen('reward');
    this.submitOfficialResult();
  };

  FinjanRushApp.prototype.submitOfficialResult = async function () {
    if (this._submitting || !this.lastResultProof) return;
    this._submitting = true; this.setNotice('');
    var retry = this.querySelector('[data-action="retry-result"]');
    retry.hidden = true;
    this.renderReward({finalScore: this.lastResultProof.finalScoreClaimed, rewardStatus: 'SUBMITTING', creditedPoints: 0});
    try {
      var result = await this.api.submit(this.lastResultProof);
      this.renderReward(result);
      this.bumpAllTimeHigh(result.finalScore || 0);
      this.hydrateHighScore();
      this.lastResultProof = null; this.api.gameToken = null;
    } catch (error) {
      this.renderReward({finalScore: this.lastResultProof.finalScoreClaimed, rewardStatus: 'UNCONFIRMED', creditedPoints: 0});
      retry.hidden = false;
      this.setNotice(this.errorMessage(error));
    } finally { this._submitting = false; }
  };

  FinjanRushApp.prototype.rewardCopy = function (record) {
    if (record.rewardStatus === 'SUBMITTING') return ['Checking your run', 'Confirming points…', 'Please keep this page open.'];
    if (record.rewardStatus === 'UNCONFIRMED') return ['Result not confirmed', 'Points not confirmed', 'Retry sends the same result; it does not start another run.'];
    if (record.rewardStatus === 'HELD_FOR_REVIEW') return ['Run received', 'Under review', 'Your run is being reviewed. No points have been added yet.'];
    if (record.decision === 'INVALID_RESULT' || record.rewardStatus === 'INVALID_RESULT') return ['Run complete', 'No points credited', 'This result could not be validated.'];
    var points = record.creditedPoints || 0;
    if (points > 0) return ['You earned', '+' + points + ' points', record.capLimitedPoints ? 'Added to your loyalty balance. Your points cap limited this reward.' : 'Added to your loyalty balance.'];
    return ['Run complete', 'No points credited', record.capLimitedPoints ? 'Your loyalty balance has reached its points cap.' : 'No Finjan Points were credited for this run.'];
  };

  FinjanRushApp.prototype.renderReward = function (record) {
    this.querySelector('[data-role="reward-score"]').textContent = record.finalScore == null ? '—' : record.finalScore;
    var copy = this.rewardCopy(record);
    this.querySelector('[data-role="reward-label"]').textContent = copy[0];
    this.querySelector('[data-role="reward-value"]').textContent = copy[1];
    this.querySelector('[data-role="reward-note"]').textContent = copy[2];
    this.querySelector('[data-role="reward-spark-left"]').hidden = !(record.creditedPoints > 0);
    this.querySelector('[data-role="reward-spark-right"]').hidden = !(record.creditedPoints > 0);
  };

  FinjanRushApp.prototype.bindReward = function () {
    this.querySelector('[data-action="retry-result"]').addEventListener('click', this.submitOfficialResult.bind(this));
  };

  FinjanRushApp.prototype.renderPlayed = function (record) {
    this.querySelector('[data-role="played-score"]').textContent = record.finalScore == null ? '—' : record.finalScore;
    this.querySelector('[data-role="played-reward"]').textContent = record.rewardStatus === 'HELD_FOR_REVIEW' ? 'Under review' : '+' + (record.creditedPoints || 0) + ' pts';
    this.querySelector('[data-role="played-note"]').textContent = this.rewardCopy(record)[2];
  };

  FinjanRushApp.prototype.bindPlayed = function () {};

  FinjanRushApp.prototype.bumpAllTimeHigh = function (score) {
    try {
      var key = CONFIG.storageKey + '.allTime';
      var current = parseInt(localStorage.getItem(key) || '0', 10) || 0;
      if (score > current) localStorage.setItem(key, String(score));
    } catch (e) { /* optional cosmetic cache, never eligibility */ }
  };

  /* ---- Development balance mode (never shown to customers) ---- */

  FinjanRushApp.prototype.buildDevPanel = function () {
    var panel = document.createElement('div');
    panel.className = 'finjan-rush__dev-panel';
    panel.innerHTML = '<h4>Balance / Dev Mode</h4><table><tbody data-role="dev-table"></tbody></table>';
    this.appendChild(panel);
    this._devPanel = panel;
    this.refreshDevPanel();
  };

  FinjanRushApp.prototype.refreshDevPanel = function () {
    if (!this.devMode || !this._devPanel) return;
    var s = this.stats;
    var frames = s.frameSamples;
    var avgFrameMs = frames.length ? frames.reduce(function (a, b) { return a + b; }, 0) / frames.length : 0;
    var fps = avgFrameMs ? Math.round(1000 / avgFrameMs) : 0;
    var totalCaught = s.normalCaught + s.goldenCaught + s.bagCaught + s.burntCaught;
    var totalSpawned = s.normalSpawned + s.goldenSpawned + s.bagSpawned + s.burntSpawned;
    var catchRate = totalSpawned ? Math.round((totalCaught / totalSpawned) * 100) : 0;


    var rows = [
      ['Score', Math.round(this.score)],
      ['Points', 'Server confirmed only'],
      ['Max combo', this.maxCombo],
      ['Normal spawned/caught', s.normalSpawned + ' / ' + s.normalCaught],
      ['Golden spawned/caught', s.goldenSpawned + ' / ' + s.goldenCaught],
      ['Bag spawned/caught', s.bagSpawned + ' / ' + s.bagCaught],
      ['Burnt spawned/caught', s.burntSpawned + ' / ' + s.burntCaught],
      ['Catch rate', catchRate + '%'],
      ['Approx FPS', fps || '—'],
      ['Duration', (CONFIG.gameDurationMs / 1000).toFixed(0) + 's'],
      // Trimmed for display only -- the actual GAME_VERSION constant used
      // everywhere else (proof object, validator) is unabbreviated.
      ['Game version', GAME_VERSION.replace('finjan-rush-', '')],
      ['Session state', this.roundClock ? this.roundClock.state : '—'],
      ['Manifest size', this.spawnManifest.length],
      ['Events logged', this.eventLog.length],
    ];
    var tbody = this._devPanel.querySelector('[data-role="dev-table"]');
    tbody.innerHTML = rows
      .map(function (r) {
        return '<tr><td>' + r[0] + '</td><td>' + r[1] + '</td></tr>';
      })
      .join('');
  };

  // Pure-function reference surface for the local Playwright test
  // harness (see the module doc comment). Nothing here is secret -- it's
  // the same logic already fully visible in this source file, exposed so
  // tests can call it directly without playing full 20-second rounds.
  // Carries no per-round state; safe to call at any time.
  window.FinjanRushTestInternals = {
    GAME_VERSION: GAME_VERSION,
    CONFIG: CONFIG,
    createSeededRandom: createSeededRandom,
    weightedPick: weightedPick,
    comboMultiplier: comboMultiplier,
    difficultyAt: difficultyAt,
    generateSpawnManifest: generateSpawnManifest,
    hashSeedToUint32: hashSeedToUint32,
    shouldFlagFrameGap: shouldFlagFrameGap,
    RoundClock: RoundClock,
  };

  window.customElements.define('finjan-rush-app', FinjanRushApp);
})();
